import React from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Toolbar, Box, Button } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { Dashboard } from "@mui/icons-material";
import { NavItem } from "./main-layout.types";

interface NavigationBarProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  theme: Theme;
  isRTL: boolean;
  user: any;
  navItems: NavItem[];
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  t,
  theme,
  isRTL,
  user,
  navItems,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <Toolbar
      disableGutters
      sx={{
        display: { xs: "none", md: "flex" },
        minHeight: 56,
        gap: 1,
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mx: "auto",
          textAlign: "center",
        }}
      >
         {navItems.map((item) => {
           if (!item.public && !user) return null;
           const isActive = location.pathname === item.path;

           return (
             <Button
               key={item.path}
               component={RouterLink}
               to={item.path}
               startIcon={item.icon}
               sx={{
                 color: isActive ? "#00d4ff" : theme.palette.text.primary,
                 position: "relative",
                 px: 3,
                 py: 1,
                 borderRadius: "8px",
                 transition: "all 0.3s",
                 fontWeight: isActive ? 600 : 400,
                 "& .MuiButton-startIcon": {
                   [isRTL ? "ml" : "mr"]: 1,
                   [isRTL ? "mr" : "ml"]: 0,
                 },
                 "&:after": {
                   content: '""',
                   position: "absolute",
                   bottom: 0,
                   left: "50%",
                   transform: "translateX(-50%)",
                   width: isActive ? "80%" : "0%",
                   height: "3px",
                   bgcolor: "#00d4ff",
                   transition: "width 0.3s",
                   borderRadius: "4px 4px 0 0",
                   boxShadow: isActive ? "0 0 10px rgba(0,212,255,0.8)" : "none",
                 },
                 "&:hover": {
                   bgcolor: "rgba(0,212,255,0.1)",
                   color: "#00d4ff",
                   "&:after": {
                     width: "80%",
                   },
                 },
               }}
             >
               {item.label}
             </Button>
           );
        })}

        {user?.role === "admin" && (
          <Button
            component={RouterLink}
            to="/admin/dashboard"
            startIcon={<Dashboard />}
            sx={{
              color: "#ff0080",
              px: 3,
              py: 1,
              borderRadius: "8px",
              border: "1px solid rgba(255,0,128,0.3)",
              bgcolor: "rgba(255,0,128,0.1)",
              transition: "all 0.3s",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "rgba(255,0,128,0.2)",
                borderColor: "#ff0080",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 20px rgba(255,0,128,0.4)",
              },
            }}
          >
            {t("nav.admin", { defaultValue: "Admin" })}
          </Button>
        )}

        {user?.role === "seller" && (
          <Button
            component={RouterLink}
            to="/seller/products"
            startIcon={<Dashboard />}
            sx={{
              color: "#00a1cc",
              px: 3,
              py: 1,
              borderRadius: "8px",
              border: "1px solid rgba(0,212,255,0.4)",
              bgcolor: "rgba(0,212,255,0.12)",
              transition: "all 0.3s",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "rgba(0,212,255,0.2)",
                borderColor: "#00d4ff",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 20px rgba(0,212,255,0.4)",
              },
            }}
          >
            {t("dashboard.sellerDashboard")}
          </Button>
        )}
      </Box>
    </Toolbar>
  );
};

export default NavigationBar;